import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PredictiveModelingPanel = ({ predictions, onRunAnalysis, analyzing }) => {
  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'text-red-600 bg-red-50 border-red-200',
      high: 'text-orange-600 bg-orange-50 border-orange-200',
      medium: 'text-yellow-600 bg-yellow-50 border-yellow-200',
      low: 'text-blue-600 bg-blue-50 border-blue-200'
    };
    return colors?.[severity] || 'text-gray-600 bg-gray-50 border-gray-200';
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 80) return 'text-green-600';
    if (confidence >= 60) return 'text-yellow-600';
    return 'text-orange-600';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Machine Learning Predictions</h3>
          <p className="text-sm text-gray-600 mt-1">AI-powered failure prediction with 24-48 hour forecast window</p>
        </div>
        <Button
          onClick={onRunAnalysis}
          disabled={analyzing}
          className="bg-purple-600 hover:bg-purple-700"
        >
          <Icon name="RefreshCw" className={`w-4 h-4 mr-2 ${analyzing ? 'animate-spin' : ''}`} />
          {analyzing ? 'Analyzing...' : 'Refresh Analysis'}
        </Button>
      </div>
      {/* Predictions List */}
      {predictions?.length > 0 ? (
        <div className="space-y-4">
          {predictions?.map((prediction, index) => (
            <div
              key={index}
              className={`border rounded-lg p-4 ${getSeverityColor(prediction?.severity)}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon
                      name={prediction?.severity === 'critical' ? 'AlertCircle' : 'AlertTriangle'}
                      className="w-5 h-5"
                    />
                    <h4 className="font-semibold text-gray-900">{prediction?.issue}</h4>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span className={`font-medium ${getConfidenceColor(prediction?.confidence)}`}>
                      {prediction?.confidence}% confidence
                    </span>
                    <span className="text-gray-600">•</span>
                    <span className="text-gray-700">{prediction?.timeframe}</span>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium uppercase ${
                  getSeverityColor(prediction?.severity)
                }`}>
                  {prediction?.severity}
                </span>
              </div>

              {/* Affected Components */}
              {prediction?.affectedComponents?.length > 0 && (
                <div className="mb-3">
                  <div className="text-xs font-medium text-gray-700 mb-2">Affected Components:</div>
                  <div className="flex flex-wrap gap-2">
                    {prediction?.affectedComponents?.map((component, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 bg-white rounded text-xs font-medium text-gray-700 border border-gray-300"
                      >
                        {component}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Preventive Actions */}
              {prediction?.preventiveActions?.length > 0 && (
                <div>
                  <div className="text-xs font-medium text-gray-700 mb-2">Recommended Actions:</div>
                  <ul className="space-y-1">
                    {prediction?.preventiveActions?.map((action, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                        <Icon name="CheckCircle" className="w-4 h-4 mt-0.5 flex-shrink-0 text-green-600" />
                        <span>{action}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <Icon name="TrendingUp" className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Predictions Available</h3>
          <p className="text-gray-600 mb-4">Run AI analysis to generate predictive insights</p>
          <Button
            onClick={onRunAnalysis}
            disabled={analyzing}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <Icon name="Sparkles" className="w-4 h-4 mr-2" />
            Run AI Analysis
          </Button>
        </div>
      )}
    </div>
  );
};

export default PredictiveModelingPanel;
